import React, { useState } from 'react';
import { MentorAreaConfig, MentorModule, MentorLesson } from '../../types/mentorship';
import { Play, Volume2, VolumeX, Clock, ChevronDown, CheckCircle2, ArrowLeft } from 'lucide-react';

interface MentorshipModulePageProps {
  config: MentorAreaConfig;
  currentModule: MentorModule;
  onSelectModule: (module: MentorModule) => void;
  onSelectLesson: (module: MentorModule, lesson: MentorLesson) => void;
  onBackToCourse: () => void;
}

export const MentorshipModulePage: React.FC<MentorshipModulePageProps> = ({
  config,
  currentModule,
  onSelectModule,
  onSelectLesson,
  onBackToCourse,
}) => {
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);

  // A primeira aula do módulo atual é a aula de destaque no Hero de fundo
  const firstLesson = currentModule.lessons[0];

  // Calcular progresso do módulo
  const completedLessons = currentModule.lessons.filter((l) => l.isCompleted).length;
  const progressPercent =
    currentModule.lessons.length > 0
      ? Math.round((completedLessons / currentModule.lessons.length) * 100)
      : 0;

  const getEmbedUrl = (url: string) => {
    if (!url) return '';
    let videoId = '';
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0] || '';
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0] || '';
    } else if (url.includes('youtube.com/embed/')) {
      return url;
    }
    return videoId
      ? `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=${isMuted ? 1 : 0}&controls=1&rel=0`
      : url;
  };

  const heroEmbedUrl = firstLesson ? getEmbedUrl(firstLesson.videoUrl) : '';

  return (
    <div className="w-full flex flex-col gap-8 pb-16 animate-fade-in select-none">
      {/* 1. Full-Bleed Hero Header (Fidelidade ao Print 2 da Hubla) */}
      <div className="relative w-full overflow-hidden bg-[#121316] select-none -mt-4 md:-mt-8 -mx-4 md:-mx-10 w-[calc(100%+2rem)] md:w-[calc(100%+5rem)]">
        <div className="relative w-full h-[60vh] min-h-[440px] max-h-[650px] bg-black overflow-hidden">
          {isPlaying && heroEmbedUrl ? (
            <iframe
              src={heroEmbedUrl}
              title={firstLesson?.title || currentModule.title}
              className="w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <>
              {/* Cover Image of 1st Lesson or Module */}
              <img
                src={firstLesson?.thumbnailUrl || currentModule.coverImageUrl}
                alt={firstLesson?.title || currentModule.title}
                className="w-full h-full object-cover object-center opacity-85 scale-105"
              />

              {/* Degradês de Fusão Suave com o Fundo (Hubla Image 2) */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#121316] via-[#121316]/75 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-b from-[#121316]/80 via-transparent to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#121316]/60 via-transparent to-transparent" />

              {/* Content Overlay */}
              <div className="absolute inset-0 px-6 md:px-16 pb-12 flex flex-col justify-end gap-3 z-10">
                {/* Back to Modules link */}
                <button
                  onClick={onBackToCourse}
                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-white mb-2 transition-colors self-start"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span>Voltar aos Módulos</span>
                </button>

                {/* Title */}
                <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-tight max-w-4xl drop-shadow-xl">
                  {firstLesson ? firstLesson.title : currentModule.title}
                </h1>

                {/* Progress line & Duration */}
                <div className="flex items-center gap-4 my-2 max-w-md">
                  <div className="flex-1 h-1 rounded-full bg-white/20 overflow-hidden">
                    <div className="w-0 h-full rounded-full bg-[#0FFF55]" />
                  </div>
                  <span className="text-xs font-semibold text-gray-300">
                    {firstLesson?.durationText || '0 min'}
                  </span>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={() => {
                      if (firstLesson) {
                        onSelectLesson(currentModule, firstLesson);
                      } else {
                        setIsPlaying(true);
                      }
                    }}
                    className="flex items-center gap-2.5 px-7 py-3 rounded-xl bg-white hover:bg-gray-100 text-black font-extrabold text-sm shadow-2xl hover:scale-105 active:scale-95 transition-all cursor-pointer"
                  >
                    <Play className="w-4 h-4 fill-black text-black" />
                    <span>Assista</span>
                  </button>

                  <button
                    onClick={() => setIsMuted(!isMuted)}
                    className="p-3 rounded-xl bg-black/40 hover:bg-black/60 backdrop-blur-md border border-white/10 text-white transition-all"
                  >
                    {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* 2. Barra de Título "Conteúdos 0%" e Dropdown Seletor de Módulo (Fidelidade ao Print 2 da Hubla) */}
      <div className="flex items-center justify-between max-w-4xl mx-auto w-full pt-2 px-2">
        <div className="flex items-center gap-3">
          <h2 className="text-lg md:text-xl font-bold text-white tracking-tight">
            Conteúdos
          </h2>
          <span className="text-xs font-semibold text-gray-400">
            {progressPercent}%
          </span>
        </div>

        {/* Dropdown Seletor de Módulos (Hubla Module Switcher) */}
        <div className="relative">
          <select
            value={currentModule.id}
            onChange={(e) => {
              const selected = config.modules.find((m) => m.id === e.target.value);
              if (selected) onSelectModule(selected);
            }}
            className="appearance-none bg-[#17181c] border border-white/15 rounded-xl px-4 py-2 pr-9 text-xs font-semibold text-gray-200 focus:outline-none focus:border-[#0FFF55] cursor-pointer transition-all"
          >
            {config.modules.map((mod) => (
              <option key={mod.id} value={mod.id} className="bg-[#17181c] text-white">
                {mod.title.toLowerCase()}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* 3. Lista Centralizada das Aulas do Módulo (Hubla Screenshot 2) */}
      <div className="max-w-4xl mx-auto w-full flex flex-col gap-3">
        {currentModule.lessons.map((les, index) => (
          <div
            key={les.id}
            onClick={() => onSelectLesson(currentModule, les)}
            className="group flex items-center justify-between gap-4 p-3.5 rounded-2xl bg-[#17181c] border border-white/10 hover:border-[#0FFF55]/50 cursor-pointer transition-all duration-300 transform hover:-translate-y-0.5 shadow-md"
          >
            <div className="flex items-center gap-4 min-w-0">
              {/* Sequencial Number */}
              <span className="text-xs font-bold text-gray-400 w-5 text-center flex-shrink-0">
                {index + 1}
              </span>

              {/* Thumbnail */}
              <div className="relative w-24 sm:w-28 aspect-video rounded-xl overflow-hidden bg-black flex-shrink-0">
                <img
                  src={les.thumbnailUrl}
                  alt={les.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 flex items-center justify-center transition-colors">
                  <Play className="w-4 h-4 text-white fill-white group-hover:scale-110 transition-transform" />
                </div>
              </div>

              {/* Lesson Title & Duration */}
              <div className="flex flex-col gap-1 min-w-0">
                <h3 className="text-xs sm:text-sm font-bold text-white group-hover:text-[#0FFF55] transition-colors truncate">
                  {les.title}
                </h3>
                <span className="flex items-center gap-1 text-[11px] text-gray-400 font-medium">
                  <Clock className="w-3 h-3 text-gray-500" />
                  {les.durationText}
                </span>
              </div>
            </div>

            {/* Checkmark Completion Status */}
            {les.isCompleted ? (
              <CheckCircle2 className="w-5 h-5 text-[#0FFF55] flex-shrink-0" />
            ) : (
              <div className="w-5 h-5 rounded-full border border-white/20 group-hover:border-[#0FFF55]/60 flex-shrink-0" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
